import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { api } from "@/lib/api";
import { Card, Skeleton, ErrorBox, Empty } from "@/components/ui";

const COLORS = ["#38bdf8", "#22c55e", "#f59e0b", "#ef4444", "#a78bfa", "#f472b6", "#34d399", "#fb923c"];

export default function Analytics() {
  const [month, setMonth] = useState<string>("");
  const [lang, setLang] = useState<"ar" | "en">("en");
  const [includeSummary, setIncludeSummary] = useState(true);
  const [includeChart, setIncludeChart] = useState(true);
  const [includeTable, setIncludeTable] = useState(true);
  const [includeNotes, setIncludeNotes] = useState(false);
  const [includeFooter, setIncludeFooter] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const q = useQuery({
    queryKey: ["analytics", month],
    queryFn: () => api.get<any>(`/analytics/monthly${month ? `?month=${month}` : ""}`),
  });

  const data = q.data;
  const diagnosisTotal = data?.diagnosis_distribution
    ? Object.values(data.diagnosis_distribution).reduce((a: number, b: any) => a + Number(b), 0)
    : 0;
  const reconciliationWarnings: string[] = data?.reconciliation?.warnings || [];

  const handleExportPDF = () => {
    const title = lang === "ar" 
      ? `إحصائية عيادة العلاج الإشعاعي — ${data?.month || "current month"}`
      : `Radiation Oncology Clinic Statistics — ${data?.month || "current month"}`;

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    let html = `<html><head><title>${title}</title>`;
    html += `<style>
      body { font-family: Arial, sans-serif; padding: 40px; }

      h1 { text-align: center; margin-bottom: 30px; }
      .summary { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-bottom: 40px; }
      .card { border: 1px solid #ccc; padding: 15px; text-align: center; }
      table { width: 100%; border-collapse: collapse; margin-top: 20px; }
      th, td { border: 1px solid #ccc; padding: 8px; text-align: left; }
      .footer { margin-top: 50px; font-size: 12px; color: #666; }
    </style></head><body>`;
    
    html += `<h1>${title}</h1>`;

    if (includeSummary && data?.workload) {
      const w = data.workload;
      html += `<div class="summary">
        <div class="card"><strong>Total Patients</strong><br>${w.total_patients || 0}</div>
        <div class="card"><strong>New Patients</strong><br>${w.new_patients || 0}</div>
        <div class="card"><strong>Follow-up Patients</strong><br>${w.follow_up_patients || 0}</div>
        <div class="card"><strong>New / Follow-up</strong><br>${w.new_patients && w.total_patients ? Math.round((w.new_patients / w.total_patients) * 100) : 0}%</div>
      </div>`;
    }

    if (includeChart && data?.diagnosis_distribution) {
      html += `<h2 style="text-align:center">Diagnosis Distribution</h2>`;
      // Simple table representation for PDF
    }

    if (includeTable && data?.diagnosis_distribution) {
      html += `<table><tr><th>Diagnosis</th><th>Count</th><th>%</th></tr>`;
      const total = Object.values(data.diagnosis_distribution).reduce((a: number, b: any) => a + b, 0);
      Object.entries(data.diagnosis_distribution).forEach(([k, v]) => {
        const pct = total > 0 ? Math.round((Number(v) / total) * 100) : 0;
        html += `<tr><td>${k}</td><td>${v}</td><td>${pct}%</td></tr>`;
      });
      html += `</table>`;
    }

    if (includeNotes) {
      html += `<div style="margin-top:40px"><strong>Notes:</strong><br><br></div>`;
    }

    if (includeFooter) {
      html += `<div class="footer">Generated on ${new Date().toLocaleDateString()} | RPIS - Radiation Oncology Clinic</div>`;
    }

    html += `</body></html>`;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-50">Radiation Oncology Clinic Statistics</h1>
          <div className="text-sm text-slate-400">عيادة العلاج الإشعاعي</div>
        </div>
        <input
          className="input w-44"
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        />
      </div>

      {q.error ? <ErrorBox error={q.error} /> : null}

      {q.isLoading || !data ? (
        <Card><Skeleton rows={8} /></Card>
      ) : (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <div className="text-sm text-slate-400">Total Patients</div>
              <div className="text-3xl font-bold mt-1">{data.workload?.total_patients ?? 0}</div>
              <div className="text-xs text-slate-500 mt-1">Unique clinic-linked patients in selected month</div>
            </Card>
            <Card>
              <div className="text-sm text-slate-400">New Patients</div>
              <div className="text-3xl font-bold mt-1">{data.workload?.new_patients ?? 0}</div>
            </Card>
            <Card>
              <div className="text-sm text-slate-400">Follow-up Patients</div>
              <div className="text-3xl font-bold mt-1">{data.workload?.follow_up_patients ?? 0}</div>
            </Card>
          </div>

          {data.clinic_filter ? (
            <Card title="Counting Rules">
              <div className="text-sm text-slate-300">
                Clinic Stats population: {data.clinic_filter.population}.
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Filter: {data.clinic_filter.start_date} to {data.clinic_filter.end_date_exclusive} (exclusive).
              </div>
            </Card>
          ) : null}

          {reconciliationWarnings.length > 0 ? (
            <Card title="Reconciliation Warning">
              <ul className="list-disc list-inside text-sm text-amber-300 space-y-1">
                {reconciliationWarnings.map((warning) => <li key={warning}>{warning}</li>)}
              </ul>
            </Card>
          ) : (
            <Card title="Reconciliation">
              <div className="text-sm text-emerald-300">Total Patients matches diagnosis distribution denominator.</div>
              <div className="text-xs text-slate-500 mt-1">
                Registry active files: {data.reconciliation?.registry_active_count ?? "—"} · Dashboard Active Patient Files: {data.reconciliation?.dashboard_active_patient_files_count ?? "—"}
              </div>
            </Card>
          )}

          {/* Advanced Section (Collapsible) */}
          <Card>
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex w-full items-center justify-between text-left"
            >
              <div className="font-semibold text-slate-100">Advanced</div>
              <div className="text-slate-400">{showAdvanced ? "Advanced ▴" : "Advanced ▾"}</div>
            </button>

            {showAdvanced && (
              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="text-slate-400">New / Follow-up %</div>
                    <div className="text-xl font-semibold mt-1">
                      {data.workload?.new_patients && data.workload?.total_patients 
                        ? Math.round((data.workload.new_patients / data.workload.total_patients) * 100) 
                        : 0}%
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-400">Visit Type Breakdown</div>
                    <div className="text-slate-500 mt-1">Not available yet</div>
                  </div>
                  <div>
                    <div className="text-slate-400">Treatment Type Breakdown</div>
                    <div className="text-slate-500 mt-1">Not available yet</div>
                  </div>
                  <div>
                    <div className="text-slate-400">No-show / Virtual</div>
                    <div className="text-slate-500 mt-1">Not available yet</div>
                  </div>
                </div>

                <div className="text-xs text-slate-500 mt-2">
                  Advanced clinic breakdowns will appear when visit_type and treatment_type are available.
                </div>
              </div>
            )}
          </Card>

          {/* Diagnosis Distribution */}
          <Card title="Diagnosis Distribution">
            {data.diagnosis_distribution && Object.keys(data.diagnosis_distribution).length > 0 ? (
              <div className="space-y-4">
                <div className="text-xs text-slate-500">Diagnosis denominator: {diagnosisTotal}</div>
                <div style={{ height: 280 }}>
                  <ResponsiveContainer>
                    <BarChart data={Object.entries(data.diagnosis_distribution).map(([name, value]) => ({ name, value }))}>
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="value" fill="#38bdf8" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <table className="table text-sm">
                  <thead>
                    <tr>
                      <th>Diagnosis</th>
                      <th>Count</th>
                      <th>%</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(data.diagnosis_distribution).map(([k, v]) => {
                      const pct = diagnosisTotal > 0 ? Math.round((Number(v) / diagnosisTotal) * 100) : 0;
                      return (
                        <tr key={k}>
                          <td>{k}</td>
                          <td>{String(v)}</td>
                          <td>{pct}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <Empty title="No diagnosis data for this month" />
            )}
          </Card>

          <Card title="Data Quality">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
              <Quality label="Missing diagnosis group" value={data.data_quality?.missing_diagnosis_group ?? 0} />
              <Quality label="Missing DOB" value={data.data_quality?.missing_dob ?? 0} />
              <Quality label="Clinic shell pending clinical data" value={data.data_quality?.clinic_shell_pending_clinical_data ?? 0} />
              <Quality label="No clinic link" value={data.data_quality?.no_clinic_link ?? 0} />
              <Quality label="MRN discrepancy" value={data.data_quality?.mrn_discrepancy ?? 0} />
            </div>
          </Card>

          {/* PDF Export Controls */}
          <Card title="Export PDF">
            <div className="space-y-4">
              <div>
                <label className="label">Language</label>
                <select className="input w-48" value={lang} onChange={(e) => setLang(e.target.value as "ar" | "en")}>
                  <option value="en">English</option>
                  <option value="ar">العربية</option>
                </select>
              </div>

              <div>
                <label className="label mb-2 block">Include in PDF</label>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={includeSummary} onChange={(e) => setIncludeSummary(e.target.checked)} /> Summary Numbers
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={includeChart} onChange={(e) => setIncludeChart(e.target.checked)} /> Diagnosis Chart
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={includeTable} onChange={(e) => setIncludeTable(e.target.checked)} /> Diagnosis Table
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={includeNotes} onChange={(e) => setIncludeNotes(e.target.checked)} /> Notes
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={includeFooter} onChange={(e) => setIncludeFooter(e.target.checked)} /> Footer
                  </label>
                </div>
              </div>

              <button onClick={handleExportPDF} className="btn-primary">
                Export PDF
              </button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

function Quality({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-line/70 bg-panel2/50 p-3">
      <div className="text-slate-400">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${value > 0 ? "text-amber-300" : "text-emerald-300"}`}>{value}</div>
    </div>
  );
}
